import React, {useState, useEffect, useContext, useCallback} from 'react';
import SurveyPageWrapper from './SurveyPageWrapper';
import SurveyResponseQuestion from './SurveyResponseQuestion';
import CodeableContext from 'contexts/CodeableContext';
import CodeOperationsContext from 'contexts/CodeOperationsContext';
import useCopyHandler from 'hooks/useCopyHandler';
import useHighlightAnswer from './useHighlightAnswer';
import {getAnswerSelect} from './surveySelectUtils';
import SurveyPagePageBar from './SurveyPagePageBar';
import queryString from 'query-string';
import SurveyQuestionNotFound from './SurveyQuestionNotFound';
import { 
  useSurveyQuestionResponses, 
  useInitializeSurveyQuestionExcerptMappings 
} from 'hooks/surveys/queries';

import {
  BLUE,
  ACTIVE_BUTTON_BLUE,
} from 'constants/ColorConstants'

const SurveyQuestionPage = ({
  history,
  match,
  location,
  projectID,
  hasEditPermission,
}) => {
  const [notFound, setNotFound] = useState(false);
  const { handleMouseDown, isAnswerDisabled } = useHighlightAnswer();

  const {
    selectedExcerptId,
    select,
    onDeselect,
    onSelectText
  } = useContext(CodeableContext);
  
  const {
    onExcerptSelected,
    getSubstring
  } = useContext(CodeOperationsContext);

  useCopyHandler(select, getSubstring);

  const surveyQuestionID = match.params.surveyQuestionID;
  const parsed = queryString.parse(location.search);
  const offset = parseInt(parsed.offset) || 0;

  const gotoResponse = useCallback((responseId) => {
    history.push(`/survey_responses/${responseId}`);
  }, [history]);

  // Use the React Query hook for survey question responses
  const { 
    data: surveyQuestionData = {}, 
    isLoading, 
    isError, 
    error 
  } = useSurveyQuestionResponses(surveyQuestionID, offset);

  const {
    survey_rows: surveyRows = [],
    next: pageNext = 0,
    project_id: surveyProjectId,
    question_text: questionText,
    id: questionId
  } = surveyQuestionData;

  // Initialize excerpt mappings for survey answers
  useInitializeSurveyQuestionExcerptMappings(surveyQuestionID, surveyRows);

  // Set notFound state based on error
  useEffect(() => {
    if (isError && (error?.status === 404 || error?.status === 403)) {
      setNotFound(true);
    }
  }, [isError, error]);

  // Deselect on question change or offset change (pagination)
  useEffect(() => {
    onDeselect();
  }, [surveyQuestionID, offset, onDeselect]);
  // Handle 404 or 403 errors
  if (notFound) {
    return <SurveyQuestionNotFound />;
  }

  const onGoNext = () => {
    history.push(`/survey_questions/${surveyQuestionID}?offset=${pageNext}`);
  }

  const onGoPrevious = () => {
    const newOffset = Math.max(0, offset - 100);
    history.push(`/survey_questions/${surveyQuestionID}?offset=${newOffset}`);
  }

  return (
    <SurveyPageWrapper
      projectID={projectID}
      hasEditPermission={hasEditPermission}
      title={questionText}
    >
      {
        surveyRows.map((surveyRow, index) => {
          const surveyAnswer = surveyRow.answer;
          
          if (!surveyAnswer || !surveyAnswer.body)
            return null;

          return (
            <div
              key={surveyRow.id}
            >
              <SurveyResponseQuestion
                responseId={surveyRow.id}
                surveyAnswer={surveyAnswer}
                questionType={"text"}
                questionId={questionId}
                questionText={surveyRow.response_text || 'Untitled'}
                onGotoResponse={gotoResponse}
                color={index % 2 === 0 ? BLUE : ACTIVE_BUTTON_BLUE}
                onSelectText={onSelectText}
                onMouseDown={handleMouseDown}
                disabled={isAnswerDisabled(surveyRow.answer?.id)}
                select={getAnswerSelect(select, surveyRow.answer?.id)}
                selectedExcerptId={selectedExcerptId}
                onExcerptSelected={onExcerptSelected}
                onDeselect={onDeselect}
                hasEditPermission={hasEditPermission}
              />

              <div
                style={{
                  marginBottom: "20px",
                  borderBottom: "1px solid #e0e0e0",
                  marginLeft: "20px",
                  marginRight: "20px"
              }}
              />
            </div>
          );
        }).filter(Boolean)
      }

      <div
        style={{
          marginLeft: "15px",
          marginBottom: "15px"
        }}>
          <SurveyPagePageBar
            onGoPrevious={onGoPrevious}
            onGoNext={onGoNext}
            pageNext={pageNext}
            offset={offset}
            disabled={isLoading}
          />
      </div>
    </SurveyPageWrapper>
  );
};

export default SurveyQuestionPage;